import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { useUiLabels } from "../context/LocaleContext";

const easeFluid = [0.32, 0.72, 0, 1] as const;

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const currentPath = location.pathname;
  const ui = useUiLabels();
  const navItems = ui.nav;

  const handleNavigation = (path: string) => {
    navigate(path);
    setIsOpen(false);
  };

  return (
    <>
      {/* Desktop — floating glass pill */}
      <motion.nav
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.75, ease: easeFluid }}
        className="pointer-events-auto fixed left-1/2 top-6 z-[20] hidden w-max max-w-[calc(100vw-2rem)] -translate-x-1/2 lg:block"
        aria-label={ui.navAriaMain}
      >
        <div className="rounded-full bg-black/45 px-2 py-2 shadow-[0_24px_80px_-40px_rgba(124,92,255,0.35)] ring-1 ring-white/[0.12] backdrop-blur-xl">
          <ul className="flex items-center gap-1">
            {navItems.map((item, index) => {
              const isActive = currentPath === item.path;
              return (
                <li key={item.id}>
                  <motion.button
                    type="button"
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.04, duration: 0.5, ease: easeFluid }}
                    onClick={() => handleNavigation(item.path)}
                    className={`relative rounded-full px-4 py-2 text-[13px] font-medium tracking-wide transition-[transform,opacity,color] duration-700 ease-fluid ${
                      isActive ? "text-white" : "text-zinc-400 hover:text-zinc-100"
                    } active:scale-[0.98]`}
                  >
                    {item.label}
                    {isActive && (
                      <motion.span
                        layoutId="navPill"
                        className="absolute inset-0 -z-10 rounded-full bg-white/[0.08] ring-1 ring-emerald-400/25"
                        transition={{ type: "spring", stiffness: 380, damping: 34 }}
                      />
                    )}
                  </motion.button>
                </li>
              );
            })}
          </ul>
        </div>
      </motion.nav>

      {/* Mobile — floating trigger + overlay menu */}
      <div className="fixed right-4 top-5 z-[20] lg:hidden">
        <motion.button
          type="button"
          aria-expanded={isOpen}
          aria-label={isOpen ? ui.navCloseMenu : ui.navOpenMenu}
          onClick={() => setIsOpen((o) => !o)}
          className="group flex h-12 w-12 items-center justify-center rounded-full bg-black/50 ring-1 ring-white/[0.12] backdrop-blur-xl transition-transform duration-700 ease-fluid active:scale-[0.96]"
        >
          <span className="relative h-[14px] w-[18px]">
            <motion.span
              className="absolute left-0 top-0 h-[1.5px] w-full rounded-full bg-zinc-100"
              animate={isOpen ? { rotate: 45, y: 6 } : { rotate: 0, y: 0 }}
              transition={{ duration: 0.45, ease: easeFluid }}
            />
            <motion.span
              className="absolute left-0 top-[6px] h-[1.5px] w-full rounded-full bg-zinc-100"
              animate={isOpen ? { opacity: 0, x: 8 } : { opacity: 1, x: 0 }}
              transition={{ duration: 0.35, ease: easeFluid }}
            />
            <motion.span
              className="absolute left-0 top-[12px] h-[1.5px] w-full rounded-full bg-zinc-100"
              animate={isOpen ? { rotate: -45, y: -6 } : { rotate: 0, y: 0 }}
              transition={{ duration: 0.45, ease: easeFluid }}
            />
          </span>
        </motion.button>
      </div>

      <AnimatePresence>
        {isOpen && (
          <>
            <motion.button
              type="button"
              aria-label={ui.navCloseMenu}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.45, ease: easeFluid }}
              className="fixed inset-0 z-[18] bg-black/75 backdrop-blur-2xl lg:hidden"
              onClick={() => setIsOpen(false)}
            />
            <motion.div
              role="dialog"
              aria-modal="true"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 24 }}
              transition={{ duration: 0.55, ease: easeFluid }}
              className="fixed inset-x-4 top-24 z-[19] max-h-[calc(100dvh-7rem)] overflow-y-auto rounded-[2rem] bg-[#0a0a0c]/95 p-6 ring-1 ring-white/[0.1] backdrop-blur-3xl lg:hidden"
            >
              <p className="mb-6 text-[10px] font-medium uppercase tracking-[0.22em] text-emerald-200/80">{ui.navMobileSection}</p>
              <ul className="flex flex-col gap-1">
                {navItems.map((item, index) => {
                  const isActive = currentPath === item.path;
                  return (
                    <li key={item.id}>
                      <motion.button
                        type="button"
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.08 + index * 0.06, duration: 0.55, ease: easeFluid }}
                        onClick={() => handleNavigation(item.path)}
                        className={`w-full rounded-2xl px-4 py-4 text-left text-lg font-semibold tracking-tight transition-[transform,opacity] duration-700 ease-fluid active:scale-[0.99] ${
                          isActive ? "bg-white/[0.08] text-white ring-1 ring-emerald-400/20" : "text-zinc-300 hover:bg-white/[0.05]"
                        }`}
                      >
                        {item.label}
                      </motion.button>
                    </li>
                  );
                })}
              </ul>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
